import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { useStore } from '~/stores/main'
import { changeRoute } from '~/composables/navigation'
import { checkUserOnBoarding, signOutUser, createAnonymousUser } from '~/composables/auth'
import { UserCookie } from '~/types/auth'

export default defineNuxtRouteMiddleware(async (to, from): Promise<void> => {
  const store = useStore()
  const app = useNuxtApp()
  const config = useRuntimeConfig()
  const router = useRouter()
  const userCookie = useCookie<UserCookie | null>('hasUser')
  const logoutCookie = useCookie<boolean>('logout')
  const cashbackInfo = useState('cashbackInfo')

  if (process.server) {
    if (userCookie.value) {
      try {
        const refreshToken = useCookie('refreshToken')
        if (refreshToken.value) {
          const authToken = useCookie('authToken')
          const { data } = await axios.post(
            `https://securetoken.googleapis.com/v1/token?key=${config.FirebaseMiioStoreWebApiKey}`,
            {
              grant_type: 'refresh_token',
              refresh_token: refreshToken.value
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            }
          )
          authToken.value = data.access_token
        }
      } catch (e: unknown) {
        Sentry.captureException(e)
        logoutCookie.value = true
      }
    } else {
      try {
        const isDev = config.public.isDev
        const headers = {
          'Accept-Language': app.$i18n.locale.value,
          'X-PRODUCT': 'STORE_WEB',
          Authorization: `Token ${config.NuxtServerAuthToken}`,
          ...(isDev
            ? {
                'CF-Access-Client-Id': config.public.CFAccessClientId,
                'CF-Access-Client-Secret': config.public.CFAccessClientSecret
              }
            : {})
        }

        const { data: storeJwt } = await axios.post(`${config.public.storeAPI}/jwt/login/`, {}, { headers })
        const { data: servicesJwt } = await axios.post(`${config.public.servicesAPI}/v3/jwt/login/`, {}, { headers })
        store.tokens = {
          store: storeJwt && storeJwt.jwt ? `Bearer ${storeJwt?.jwt}` : null,
          services: servicesJwt && servicesJwt.jwt ? `Bearer ${servicesJwt?.jwt}` : null
        }
      } catch (e: unknown) {
        Sentry.captureException(e)
      }
    }
  }

  if (process.client) {
    if (logoutCookie.value) {
      store.loading = true
      logoutCookie.value = false
      await signOutUser(true)
    } else if ((!store.tokens || !store.tokens?.store || !store.tokens?.services) && !userCookie.value) {
      store.loading = true
      await createAnonymousUser()
    }
  }

  router.onError((error, to) => {
    if (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Cannot find module') ||
      error.message.includes('Unable to preload CSS') ||
      error.message.includes('Cannot find static asset')
    ) {
      window.location = to.fullPath
    }
  })

  const loginPage: boolean = to.fullPath.includes('login')
  const registerPage: boolean = to.fullPath.includes('register')
  const accountPage: boolean = to.path.includes('account')
  const checkoutPage: boolean = to.path.includes('checkout')
  const requireLoginValidation: boolean = store.requireLoginValidation.phoneNumber || store.requireLoginValidation.country

  const allowedAccountPages: string[] = ['favorites', 'faq', 'privacy-policy', 'conflict-resolution', 'terms-conditions']

  if (checkoutPage && (!store.basket || store.basket?.lines.length === 0)) {
    changeRoute('basket')
  }

  if (store.miioUser) {
    const checkOnboarding: boolean = checkUserOnBoarding()
    if (loginPage || registerPage) {
      changeRoute('account-profile')
    } else if (accountPage) {
      if (requireLoginValidation) {
        changeRoute('update-user', { redirect: 'account' })
      } else if (checkOnboarding) {
        changeRoute('onboarding')
      }
    } else if (checkoutPage && requireLoginValidation) {
      changeRoute('update-user', { redirect: 'checkout' })
    }
  }

  if ((!userCookie.value || userCookie.value === UserCookie.AnonymousUser) && accountPage && !checkIfStringsOfListIncludes(allowedAccountPages, to.path)) {
    changeRoute('index')
  }

  if (to.path.includes('update-user') && !requireLoginValidation) {
    changeRoute('account-profile')
  }

  if (from.path.includes('checkout') && to.path.includes('checkout')) {
    changeRoute('basket')
  }

  if (to.fullPath.includes('compare')) {
    if (!store.compareProducts.length) {
      changeRoute('products')
    }
  } else {
    store.compareProducts = []
  }

  if ((to.name === 'order-success' || to.name === 'order-failed') && !store.order) {
    changeRoute('basket')
  }

  if (from.name === 'order-success' || from.name === 'order-failed') {
    cashbackInfo.value = null
  }
})
